// Spec 082 — a META de período de uma linha.
//
// A meta DIÁRIA não é declarada: ela é derivada dividindo esta pelos dias úteis do calendário, e a
// derivação é PREMISSA, não medição. O cliente só tem meta de estoque mensal, e dividir por dias
// úteis é a melhor derivação disponível — a tela diz isso com todas as letras.
import { z } from "zod";
import { UuidSchema } from "./common.js";

export const TargetBasisSchema = z.enum(["WEEKLY", "MONTHLY"]);
export type TargetBasis = z.infer<typeof TargetBasisSchema>;

const CIVIL_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Segunda-feira, calculada SEM fuso: a data civil basta, e envolver fuso aqui traria de volta o
 * defeito que `wallTimeToUtc` existe para evitar. `Date.UTC` sobre a data pura é determinístico.
 */
function isMonday(day: string): boolean {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay() === 1;
}

export const LineProductionTargetRequestSchema = z
  .object({
    basis: TargetBasisSchema,
    periodStart: z.string().regex(CIVIL_DAY, "Use uma data no formato aaaa-mm-dd."),
    quantity: z.number().finite().positive(),
  })
  .strict()
  .refine((t) => (t.basis === "MONTHLY" ? t.periodStart.endsWith("-01") : true), {
    message: "A meta mensal começa no dia 1.",
    path: ["periodStart"],
  })
  .refine((t) => (t.basis === "WEEKLY" ? isMonday(t.periodStart) : true), {
    message: "A meta semanal começa numa segunda-feira.",
    path: ["periodStart"],
  });
export type LineProductionTargetRequest = z.infer<typeof LineProductionTargetRequestSchema>;

export const LineProductionTargetPatchSchema = z
  .object({ quantity: z.number().finite().positive() })
  .strict();
export type LineProductionTargetPatch = z.infer<typeof LineProductionTargetPatchSchema>;

export const LineProductionTargetResponseSchema = z.object({
  id: UuidSchema,
  lineId: UuidSchema,
  basis: TargetBasisSchema,
  periodStart: z.string(),
  quantity: z.number().finite(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LineProductionTargetResponse = z.infer<typeof LineProductionTargetResponseSchema>;

export const ListTargetsQuerySchema = z.object({
  from: z.string().regex(CIVIL_DAY).optional(),
  to: z.string().regex(CIVIL_DAY).optional(),
});
export type ListTargetsQuery = z.infer<typeof ListTargetsQuerySchema>;

/**
 * De onde veio o planejado de uma execução. Campos IRMÃOS, no mesmo espírito de `plannedBasis` e
 * `calendarSource`: a origem NUNCA é um quarto valor de `plannedQtyBasis`, porque isso quebraria
 * todo consumidor que casa contra os três.
 */
export const PlannedQtyBasisSchema = z.enum(["target", "manual", "none"]);
export type PlannedQtyBasis = z.infer<typeof PlannedQtyBasisSchema>;

export const PlannedQtyTargetSchema = z.object({
  id: UuidSchema,
  basis: TargetBasisSchema,
  periodStart: z.string(),
  quantity: z.number().finite(),
  /**
   * O divisor efetivamente usado, GRAVADO na importação. Recalcular na leitura faria a frase mudar
   * quando alguém edita um feriado — e a meta que a execução recebeu não muda mais.
   */
  businessDays: z.number().int().positive(),
});
export type PlannedQtyTarget = z.infer<typeof PlannedQtyTargetSchema>;
